import { useEffect, useRef, useState } from "react";

/* Visina tabele koja se zavrsava na dnu ekrana — i broj redova koji tu visinu
 * STVARNO popunjava.
 *
 * Visina se RACUNA, ne pogadja. Iznad tabele stoji promenljiva visina —
 * zaglavlje aplikacije, kartice modula i traka sa izborom kolona, koja se
 * prelomi u dva reda kad je izabrano vise kolona, a na mobilnom se slaze
 * uspravno. Fiksna vrednost (npr. 600px) je zato na malom ekranu probijala dno,
 * a na velikom ostavljala prazan prostor ispod spiska.
 *
 * Meri se gde tabela stvarno pocinje, pa se od visine prozora oduzme to i njeno
 * sopstveno podnozje. Racuna se pri montiranju, na promenu velicine prozora i
 * na svaku prosledjenu zavisnost (npr. izbor kolona) koja pomera tabelu.
 *
 * ZASTO I BROJ REDOVA (03.11.2026): `scrollHeight` je samo GORNJA GRANICA za
 * telo tabele. Dok stranica ima `rows={10}`, telo je visoko koliko deset redova
 * i veca granica ne menja nista — ispod spiska ostaje prazno pola ekrana. Tek
 * broj redova po strani odredjuje koliko tabela stvarno zauzme, pa ga ova kuka
 * racuna iz izmerene visine i visine JEDNOG reda.
 *
 * Visina reda se MERI iz DOM-a, ne pretpostavlja: zavisi od teme (retro ima
 * svoje pismo), od `size="small"` i od toga da li se sadrzaj celije prelama u
 * dva reda. Dok tabela jos nema nijedan red (podaci se ucitavaju), uzima se 40px.
 *
 * NIZ PODATAKA NE TREBA MEDJU ZAVISNOSTIMA. Prvi pokusaj je bio da se prosledi
 * (`useVisinaTabele([podaci, ...])`), da bi se visina reda premerila cim podaci
 * stignu — ali kuka se zove na vrhu komponente, a izvedeni niz (`useMemo`,
 * filtriranje) je cesto deklarisan tek stotinak linija nize. JavaScript to ne
 * oprasta: citanje `const`-a pre njegove deklaracije je `ReferenceError`
 * („Cannot access … before initialization"), pa je cela stranica ostajala bela.
 * Prevodjenje to ne hvata — kod je sintaksno ispravan. Zato ucitavanje podataka
 * hvata `ResizeObserver` nad omotacem: kad tabela promeni visinu (stigli redovi,
 * prelomila se traka iznad), racun se ponovi sam.
 *
 * Koristi se ovako:
 *   const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([selectedColumns]);
 *   <div ref={omotTabele}>
 *     <DataTable paginator rows={redovaTabele} scrollHeight={visinaTabele} ... />
 *   </div>
 *
 * podnozje: zaglavlje tabele i paginator su UNUTAR tabele, a scrollHeight vazi
 * samo za telo — zato se oduzimaju, uz mali predah do ivice ekrana. Tabela ispod
 * koje u istoj kartici stoji jos nesto (zbir, traka radnji) prosledjuje vecu
 * vrednost, inace taj red padne ispod ivice ekrana.
 */

/* Visina reda kad se jos nema sta izmeriti. Van opsega 20–90px merenje se
   odbacuje: tolika vrednost znaci da je uhvacen red koji nije obican (prazna
   poruka preko cele tabele, red sa slikom). */
const RED_PODRAZUMEVANO = 40;
const RED_NAJMANJE = 20;
const RED_NAJVISE = 90;

export default function useVisinaTabele(zavisnosti = [], podnozje = 112) {
    const omot = useRef(null);
    /* Do prvog merenja stoje razumne vrednosti — da tabela ne trepne od nule. */
    const [visina, setVisina] = useState("60vh");
    const [redova, setRedova] = useState(10);

    useEffect(() => {
        const izracunaj = () => {
            const vrh = omot.current?.getBoundingClientRect().top;
            if (vrh == null) return;
            const slobodno = window.innerHeight - vrh - podnozje;
            /* Donja granica: na niskom prozoru tabela sme da izadje iz okvira,
               ali ne sme da se svede na par piksela i postane neupotrebljiva. */
            const telo = Math.max(220, Math.round(slobodno));
            setVisina(telo + "px");

            const prvi = omot.current?.querySelector(".p-datatable-tbody > tr");
            const izmerena = prvi ? prvi.getBoundingClientRect().height : 0;
            const visinaReda = izmerena >= RED_NAJMANJE && izmerena <= RED_NAJVISE
                ? izmerena
                : RED_PODRAZUMEVANO;

            /* Gornja granica postoji zbog troska iscrtavanja: na vrlo visokom
               ekranu nema smisla praviti stranicu od dve stotine redova. */
            setRedova(Math.min(100, Math.max(5, Math.floor(telo / visinaReda))));
        };

        izracunaj();
        window.addEventListener("resize", izracunaj);

        /* Tabela sama javlja kad se promenila — tako se hvata trenutak kad
           podaci stignu, bez zavisnosti od niza podataka (vidi gore zasto).
           Racun ide u sledeci kadar: `ResizeObserver` koji menja raspored u
           svom pozivu pregledac prijavljuje kao „loop completed with undelivered
           notifications". */
        let zakazano = 0;
        const posmatrac = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
            if (zakazano) return;
            zakazano = requestAnimationFrame(() => { zakazano = 0; izracunaj(); });
        });
        if (posmatrac && omot.current) posmatrac.observe(omot.current);

        return () => {
            window.removeEventListener("resize", izracunaj);
            if (zakazano) cancelAnimationFrame(zakazano);
            if (posmatrac) posmatrac.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [podnozje, ...zavisnosti]);

    return [omot, visina, redova];
}
